import React from "react";
import DOMPurify from "isomorphic-dompurify";
import { desktopRightContainerWidth } from "@utils/index";
import { resolvePath } from "@utils/resolvePath";

export type PlanFeature = {
  text: string;
  image?: string;
};

export type PlanCard = {
  id: string;
  badge?: string;
  badgeVariant?: "neutral" | "upgrade";
  title: string;
  price: string;
  priceNudge?: string;
  selected?: boolean;
  features?: PlanFeature[];
};

export type BenefitCard = {
  id: string;
  tag?: string;
  title: string;
  description: string;
  selected?: boolean;
  disabled?: boolean;
  hasVariants?: boolean;
};

export type FooterConfig = {
  desktopEyebrow?: string;
  desktopCtaLabel?: string;
  desktopActionKey?: string;
  mobileTaxLabel?: string;
  mobileCtaLabel?: string;
  mobileActionKey?: string;
  actionKey?: string;
};

type TpOnlyRenewalsUIProps = {
  title?: string;
  planSectionTitle?: string;
  benefitsSectionTitle?: string;
  plans: PlanCard[];
  benefits: BenefitCard[];
  footer?: FooterConfig;
  isDesktop: boolean;
  isSubmitting: boolean;
  isCtaDisabled: boolean;
  ctaLabel?: string;
  footerPrice?: string;
  onSelectPlan: (planId: string) => void;
  onToggleBenefit: (benefit: BenefitCard) => void;
  onCtaClick: () => void;
};

const sanitize = (value: string) => ({
  __html: DOMPurify.sanitize(value)
});

const getAssetPath = (src?: string) => {
  if (!src) return "";
  if (/^https?:\/\//.test(src)) return src;
  return src.startsWith("/") ? resolvePath(src) : "";
};

const TickIcon = ({ src }: { src?: string }) => (
  <img
    className="tp-only-renewals__tick"
    src={getAssetPath(src) || getAssetPath("/images/tick_do_yes_proceed.png")}
    alt=""
  />
);

const TpOnlyRenewalsUI = ({
  title,
  planSectionTitle,
  benefitsSectionTitle,
  plans,
  benefits,
  footer,
  isDesktop,
  isSubmitting,
  isCtaDisabled,
  ctaLabel,
  footerPrice,
  onSelectPlan,
  onToggleBenefit,
  onCtaClick
}: TpOnlyRenewalsUIProps) => {
  return (
    <>
      <section className="tp-only-renewals">
        {title ? <h1>{title}</h1> : null}

        <div className="tp-only-renewals__section">
          {planSectionTitle ? <h2>{planSectionTitle}</h2> : null}
          <div className="tp-only-renewals__plans">
            {plans.map((plan) => (
              <article
                aria-checked={Boolean(plan.selected)}
                aria-disabled={isSubmitting}
                className={`tp-only-renewals__plan${
                  plan.selected ? " tp-only-renewals__plan--selected" : ""
                }`}
                key={plan.id}
                onClick={() => {
                  if (!isSubmitting) onSelectPlan(plan.id);
                }}
                onKeyDown={(event) => {
                  if (
                    !isSubmitting &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    onSelectPlan(plan.id);
                  }
                }}
                role="radio"
                tabIndex={isSubmitting ? -1 : 0}
              >
                <div className="tp-only-renewals__plan-header">
                  <div className="tp-only-renewals__plan-title">
                    {plan.badge ? (
                      <span
                        className={`tp-only-renewals__badge tp-only-renewals__badge--${
                          plan.badgeVariant || "neutral"
                        }`}
                      >
                        {plan.badge}
                      </span>
                    ) : null}
                    {plan.title ? <h3>{plan.title}</h3> : null}
                  </div>
                  <div className="tp-only-renewals__price">
                    <strong>{plan.price}</strong>
                    {plan.priceNudge ? <span>{plan.priceNudge}</span> : null}
                  </div>
                </div>

                {plan.features?.length ? (
                  <ul className="tp-only-renewals__features">
                    {plan.features.map((feature, index) => (
                      <li key={`${plan.id}-${feature.text}-${index}`}>
                        <TickIcon src={feature.image} />
                        <span
                          dangerouslySetInnerHTML={sanitize(feature.text)}
                        />
                      </li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>
        </div>

        <div className="tp-only-renewals__section">
          {benefitsSectionTitle ? <h2>{benefitsSectionTitle}</h2> : null}
          <div className="tp-only-renewals__benefits">
            {benefits.map((benefit) => {
              const isSelected = Boolean(benefit.selected);

              return (
                <article className="tp-only-renewals__benefit" key={benefit.id}>
                  {benefit.tag ? (
                    <span className="tp-only-renewals__law-tag">
                      {benefit.tag}
                    </span>
                  ) : null}
                  <div className="tp-only-renewals__benefit-header">
                    {benefit.title ? <h3>{benefit.title}</h3> : null}
                    {benefit.title ? (
                      <button
                        aria-pressed={isSelected}
                        aria-label={`${benefit.title}`}
                        className={`tp-only-renewals__switch${
                          isSelected ? " tp-only-renewals__switch--on" : ""
                        }`}
                        disabled={benefit.disabled || isSubmitting}
                        onClick={() => onToggleBenefit(benefit)}
                        type="button"
                      >
                        <span />
                      </button>
                    ) : null}
                  </div>
                  {benefit.description ? (
                    <p
                      dangerouslySetInnerHTML={sanitize(benefit.description)}
                    />
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {footer ? (
        <div className="tp-only-renewals__footer">
          {isDesktop ? (
            <div
              aria-hidden="true"
              className="tp-only-renewals__footer-spacer"
            />
          ) : null}
          <div className="tp-only-renewals__footer-inner">
            <div className="tp-only-renewals__footer-price">
              {isDesktop && footer.desktopEyebrow ? (
                <span>{footer.desktopEyebrow}</span>
              ) : null}
              <strong>{footerPrice}</strong>
              {!isDesktop && footer.mobileTaxLabel ? (
                <em>{footer.mobileTaxLabel}</em>
              ) : null}
            </div>
            {ctaLabel ? (
              <button
                type="button"
                disabled={isSubmitting || isCtaDisabled}
                onClick={onCtaClick}
              >
                {ctaLabel}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* styled-jsx adds the jsx attribute at compile time. */}
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx>{`
        .tp-only-renewals {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: ${isDesktop ? "24px" : "24px"};
          color: #040222;
          box-sizing: border-box;
        }

        .tp-only-renewals h1,
        .tp-only-renewals h2,
        .tp-only-renewals h3,
        .tp-only-renewals p {
          margin: 0;
        }

        .tp-only-renewals > h1 {
          font-size: ${isDesktop ? "24px" : "20px"};
          font-weight: 600;
          line-height: ${isDesktop ? "32px" : "28px"};
          letter-spacing: -0.1px;
        }

        .tp-only-renewals__section {
          display: flex;
          width: 100%;
          flex-direction: column;
          gap: 12px;
        }

        .tp-only-renewals__section > h2 {
          color: #5b5675;
          font-size: 14px;
          font-weight: 700;
          line-height: 22px;
          letter-spacing: 1px;
        }

        .tp-only-renewals__plans,
        .tp-only-renewals__benefits {
          display: flex;
          width: 100%;
          flex-direction: column;
          gap: 12px;
        }

        .tp-only-renewals__plan {
          display: flex;
          width: 100%;
          flex-direction: column;
          justify-content: center;
          gap: 12px;
          padding: 12px 16px;
          border: 1px solid #e7e7f0;
          border-radius: ${isDesktop ? "24px" : "24px"};
          background: #ffffff;
          box-sizing: border-box;
          cursor: pointer;
          outline: none;
        }

        .tp-only-renewals__plan:focus-visible {
          box-shadow: 0 0 0 3px rgba(89, 32, 197, 0.2);
        }

        .tp-only-renewals__plan--selected {
          border-color: #0fa457;
          background: rgba(235, 251, 238, 0.3);
        }

        .tp-only-renewals__plan-header {
          display: flex;
          width: 100%;
          justify-content: space-between;
          gap: 12px;
        }

        .tp-only-renewals__plan-title {
          display: flex;
          min-width: 0;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
        }

        .tp-only-renewals__badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 16px;
          padding: 0 8px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 500;
          line-height: 16px;
          white-space: nowrap;
        }

        .tp-only-renewals__badge--neutral {
          color: #040222;
          background: #f0f0f6;
        }

        .tp-only-renewals__badge--upgrade {
          color: #ffffff;
          background: linear-gradient(266deg, #f66969 10.35%, #743ddc 106.34%);
        }

        .tp-only-renewals__plan h3 {
          color: #5920c5;
          font-size: 18px;
          font-weight: 600;
          line-height: 24px;
        }

        .tp-only-renewals__price {
          display: flex;
          flex: 0 0 auto;
          flex-direction: column;
          align-items: flex-end;
          gap: 3px;
        }

        .tp-only-renewals__price strong {
          color: #040222;
          font-size: 16px;
          font-weight: 600;
          line-height: 22px;
          white-space: nowrap;
        }

        .tp-only-renewals__price span {
          min-width: 88px;
          padding: 4px 0 4px 8px;
          border-radius: 49px;
          color: #0a562a;
          background: linear-gradient(
            94deg,
            rgba(74, 222, 128, 0.32) 0.48%,
            rgba(74, 222, 128, 0) 89.37%
          );
          font-size: 11px;
          font-weight: 500;
          line-height: 14px;
          text-align: right;
          white-space: nowrap;
          box-sizing: border-box;
        }

        .tp-only-renewals__features {
          display: flex;
          width: 100%;
          flex-direction: column;
          gap: ${isDesktop ? "4px" : "4px"};
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .tp-only-renewals__features li {
          display: flex;
          align-items: flex-start;
          gap: 4px;
          color: #36354c;
          font-size: 12px;
          font-weight: 400;
          line-height: 18px;
        }

        .tp-only-renewals__features li :global(b),
        .tp-only-renewals__features li :global(strong) {
          font-weight: 500;
        }

        .tp-only-renewals__tick {
          display: inline-flex;
          width: 16px;
          height: 16px;
          flex: 0 0 16px;
          margin-top: 1px;
          object-fit: contain;
        }

        .tp-only-renewals__benefit {
          display: flex;
          width: 100%;
          flex-direction: column;
          gap: 8px;
          padding: 12px;
          border: 1px solid #e7e7f0;
          border-radius: 12px;
          background: #ffffff;
          box-sizing: border-box;
        }

        .tp-only-renewals__law-tag {
          width: max-content;
          max-width: 100%;
          padding: 6px 12px;
          border-radius: 24px;
          color: #d83d37;
          background: #fbeaea;
          font-size: 12px;
          font-weight: 600;
          line-height: 18px;
        }

        .tp-only-renewals__benefit-header {
          display: flex;
          width: 100%;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        .tp-only-renewals__benefit h3 {
          min-width: 0;
          color: #040222;
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
          letter-spacing: -0.1px;
        }

        .tp-only-renewals__benefit p {
          color: #5b5675;
          font-size: 12px;
          font-weight: 400;
          line-height: 18px;
        }

        .tp-only-renewals__benefit p :global(strong) {
          font-weight: 700;
        }

        .tp-only-renewals__switch {
          position: relative;
          flex: 0 0 32px;
          width: 32px;
          height: 20px;
          padding: 0;
          border: 0;
          border-radius: 999px;
          background: #e7e7f0;
          cursor: pointer;
          transition: background 160ms ease;
        }

        .tp-only-renewals__switch span {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 4px 6px -2px rgba(54, 53, 76, 0.08);
          transition: transform 160ms ease;
        }

        .tp-only-renewals__switch--on {
          background: #0fa457;
        }

        .tp-only-renewals__switch--on span {
          transform: translateX(12px);
        }

        .tp-only-renewals__switch:disabled {
          cursor: not-allowed;
          opacity: 0.65;
        }

        .tp-only-renewals__footer {
          position: fixed;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 10;
          display: flex;
          justify-content: center;
          pointer-events: none;
        }

        .tp-only-renewals__footer-spacer {
          width: 20%;
        }

        .tp-only-renewals__footer-inner {
          width: ${isDesktop ? desktopRightContainerWidth : "100%"};
          max-width: ${isDesktop ? "calc(100vw - 48px)" : "none"};
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: ${isDesktop ? "32px" : "10px"};
          padding: ${isDesktop ? "16px 20px" : "12px 20px 20px"};
          border-radius: ${isDesktop ? "16px 16px 0 0" : "0"};
          background: #ffffff;
          box-shadow: 0 -6px 24px rgba(4, 2, 34, 0.08);
          box-sizing: border-box;
          pointer-events: auto;
        }

        .tp-only-renewals__footer-price {
          display: flex;
          flex-direction: ${isDesktop ? "column" : "row"};
          align-items: ${isDesktop ? "flex-start" : "flex-end"};
          gap: ${isDesktop ? "0" : "6px"};
          min-width: 0;
        }

        .tp-only-renewals__footer-price span {
          color: #5b5675;
          font-size: 12px;
          font-weight: 400;
          line-height: 18px;
        }

        .tp-only-renewals__footer-price strong {
          color: #040222;
          font-size: ${isDesktop ? "24px" : "24px"};
          font-weight: 600;
          line-height: 32px;
          white-space: nowrap;
        }

        .tp-only-renewals__footer-price em {
          margin-bottom: 5px;
          color: #5b5675;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: 18px;
          white-space: nowrap;
        }

        .tp-only-renewals__footer button {
          width: ${isDesktop ? "214px" : "148px"};
          max-width: 52%;
          min-height: 48px;
          border: 0;
          border-radius: 8px;
          background: #0fa457;
          color: #ffffff;
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
          cursor: pointer;
        }

        .tp-only-renewals__footer button:disabled {
          cursor: not-allowed;
          opacity: 0.65;
        }
      `}</style>
    </>
  );
};

export default TpOnlyRenewalsUI;
